import { Button, ConfirmationModal, Typography } from "@platform/design-system";
import { Stack } from "@mui/material";
import {
  proposalQueryKeys,
  useDeleteProposalPlan,
  useDismissPlaceholderProposal,
  useRestoreProposalPlan,
  useSuppressPadSlots,
} from "@platform/mpx-quotes-grid";
import { useCallback } from "react";
import { useQueryClient } from "@tanstack/react-query";

import useToastNotificationService from "../../../../../../../hooks/useToastNotificationService";
import { DeletePlanInfo } from "../hooks/usePlanMenuOptions";

type DeletePlanDialogProps = {
  open: boolean;
  onCancel: () => void;
  planInfo?: DeletePlanInfo;
  rfpId: string;
};

export const DeletePlanDialog: React.FC<DeletePlanDialogProps> = ({
  open,
  onCancel,
  planInfo,
  rfpId,
}) => {
  const queryClient = useQueryClient();
  const queryKey = proposalQueryKeys.all(rfpId);
  const { createToastNotification } = useToastNotificationService();

  const deletePlan = useDeleteProposalPlan({
    onError: (err, _override, _context) => {
      if (err instanceof Error) {
        createToastNotification({
          severity: "error",
          message: `Failed to delete plan: ${err.message}`,
        });
      }
    },
  });
  const restorePlan = useRestoreProposalPlan({ onSuccess: () => {} });
  const dismissPlaceholder = useDismissPlaceholderProposal();
  const suppressPadSlots = useSuppressPadSlots({ onSuccess: () => {} });

  const handleConfirm = useCallback(() => {
    if (!planInfo) return;

    const notifyDeleted = (undo?: () => void): void => {
      onCancel();
      queryClient.invalidateQueries({ queryKey });
      const { close } = createToastNotification({
        severity: "success",
        message: (
          <Stack spacing={1} paddingTop={1}>
            <Typography>
              Plan: <strong>{planInfo.name}</strong> has been successfully
              deleted from <strong>{planInfo.groupName}</strong> Plan Group
            </Typography>

            {undo ? (
              <Button
                variant="tertiary"
                onClick={() => {
                  try {
                    undo();
                  } finally {
                    close();
                  }
                }}
                sx={{ textDecoration: "underline", width: "fit-content" }}
              >
                Undo
              </Button>
            ) : null}
          </Stack>
        ),
        title: "Plan Deleted",
      });
    };

    // A placeholder column and a pad slot are both synthesized at read time —
    // there is no proposal_plan row to hard-delete, so each routes to the
    // mechanism that stops the backend from re-synthesizing it (same routing
    // the edit/save path uses for these cells).
    switch (planInfo.kind) {
      case "placeholder":
        dismissPlaceholder.mutate(
          { rfpId, target: planInfo.target },
          { onSuccess: () => notifyDeleted() },
        );
        break;
      case "pad":
        suppressPadSlots.mutate(
          { id: planInfo.target.proposalPlanGroupId, padSuppressed: true },
          {
            onSuccess: () =>
              notifyDeleted(() =>
                suppressPadSlots.mutate(
                  {
                    id: planInfo.target.proposalPlanGroupId,
                    padSuppressed: false,
                  },
                  {
                    onSuccess: () =>
                      queryClient.invalidateQueries({ queryKey }),
                  },
                ),
              ),
          },
        );
        break;
      case "real":
        deletePlan.mutate(
          { id: planInfo.id },
          {
            onSuccess: () =>
              notifyDeleted(() =>
                restorePlan.mutate(
                  { id: planInfo.id },
                  {
                    onSuccess: () =>
                      queryClient.invalidateQueries({ queryKey }),
                  },
                ),
              ),
          },
        );
        break;
    }
  }, [
    createToastNotification,
    deletePlan,
    dismissPlaceholder,
    onCancel,
    planInfo,
    queryClient,
    queryKey,
    restorePlan,
    rfpId,
    suppressPadSlots,
  ]);

  return (
    <ConfirmationModal
      open={open}
      onCancel={onCancel}
      onAccept={handleConfirm}
      description={`Are you sure you want to remove the ${planInfo?.name} from ${planInfo?.groupName}?`}
      confirmButtonText="Delete"
      cancelButtonText="Cancel"
      title="Delete Plan?"
      ConfirmButtonProps={{ variant: "danger" }}
      CancelButtonProps={{ variant: "tertiary" }}
      loading={
        deletePlan.isPending ||
        restorePlan.isPending ||
        dismissPlaceholder.isPending ||
        suppressPadSlots.isPending
      }
    />
  );
};
